import React from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormHelperText,
  InputLabel,
  Box,
  Typography,
} from '@mui/material';
import CbButton from '../../../../components/Buttons/CbButton';
import TextFieldBase from '../../../../components/inputs/text-fields/base/TextFieldBase';
import { submissionReasons } from '../../../_statics/decline.statics';
import { declineSubmission } from '../../../../api/submissions/submissions.api';
import useNotifications from '../../../../_lib/notifications/useNotifications';

const schema = Yup.object().shape({
  declinationReason: Yup.string().required('Declination reason is required'),
  declinationReasonDescription: Yup.string().required(
    'Declination note is required'
  ),
});

const DeclineSubmissionModal = ({
  data,
  close,
}: {
  data: {
    submissionId: string;
    accountId: string;
    submissionNumber: string;
    accountName: string;
  };
  close: () => void;
}) => {
  const notifications = useNotifications();
  const methods = useForm({
    defaultValues: {
      declinationReason: '',
      declinationReasonDescription: '',
    },
    resolver: yupResolver(schema),
  });

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = methods;

  const onSubmit = async (formData: any) => {
    try {
      await declineSubmission(data.submissionId, {
        data: {
          declinationReason: formData.declinationReason,
          declinationReasonDescription: formData.declinationReasonDescription,
        },
      });
      notifications.success('Submission declined successfully.');
      close();
    } catch (err) {
      notifications.error('Unable to decline submission.');
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Box mb={3} mt={2} textAlign="center">
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: '1.1rem',
              color: 'text.primary',
              fontFamily: (theme) => theme.typography.fontFamily,
              mb: 2,
            }}
          >
            Are you sure you want to ‘Decline’ this submission?
          </Typography>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '1.35rem',
              color: 'text.primary',
              fontFamily: (theme) => theme.typography.fontFamily,
              letterSpacing: 0.5,
              textAlign: 'center',
            }}
          >
            {data.submissionNumber}: {data.accountName}
          </Typography>
        </Box>
        <DialogContent style={{ padding: '2rem 3rem', minWidth: 400 }}>
          <Box mb={2}>
            <InputLabel required id="decline-reason-label">
              Declination Reason
            </InputLabel>
            <Select
              labelId="decline-reason-label"
              fullWidth
              {...register('declinationReason')}
              defaultValue=""
              error={!!errors.declinationReason}
            >
              {submissionReasons.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {errors.declinationReason && (
              <FormHelperText error>
                {errors.declinationReason.message}
              </FormHelperText>
            )}
          </Box>
          <Box mb={2}>
            <TextFieldBase
              label="Declination Note"
              required
              multiline
              minRows={4}
              errorMessage={errors.declinationReasonDescription?.message}
              inputProps={{ maxLength: 500 }}
              {...register('declinationReasonDescription')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <CbButton
            styleName="cancel"
            onClick={close}
            buttonText="Cancel"
            disabled={isSubmitting}
          />
          <CbButton
            styleName="ctaButton"
            type="submit"
            buttonText="Decline"
            loading={isSubmitting}
          />
        </DialogActions>
      </form>
    </FormProvider>
  );
};

export default DeclineSubmissionModal;
